import { AuthProvider, useAuth } from "@laborelec/lbe-react/auth";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { setAuthToken } from "@shared/api/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useRef } from "react";
import { appTheme } from "../theme/appTheme";
import { UserProvider } from "./UserContext";

const CONNECTIONS = {
  "external.engie.com": import.meta.env.VITE_OAUTH_CONNECTION_ID_ENGIE!,
  "engie.com": import.meta.env.VITE_OAUTH_CONNECTION_ID_ENGIE!,
  rest: import.meta.env.VITE_OAUTH_CONNECTION_ID_ENGIE_LABORELEC!,
};

const authConfig = {
  relevantScope: "pid-kks-extractor",
  connections: CONNECTIONS,
  okta: {
    authorizationUrl: import.meta.env.VITE_OAUTH_URL!,
    clientId: import.meta.env.VITE_OAUTH_CLIENT_ID!,
    scope: "pid-kks-extractor openid profile email",
  },
};

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
});

function AuthTokenBridge({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  // Applied during render so it is set before any React Query request fires
  // on the same cycle (useEffect would be too late).
  const lastAppliedToken = useRef<string | null | undefined>(undefined);
  if (lastAppliedToken.current !== token) {
    lastAppliedToken.current = token;
    setAuthToken(token);
  }
  return <>{children}</>;
}

export default function AppProviders({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <AuthProvider config={authConfig}>
      <QueryClientProvider client={queryClient}>
        <AuthTokenBridge>
          <UserProvider>
            <ThemeProvider theme={appTheme}>
              <CssBaseline />
              {children}
            </ThemeProvider>
          </UserProvider>
        </AuthTokenBridge>
      </QueryClientProvider>
    </AuthProvider>
  );
}
